import { t } from "@travily/i18n";
import { Wizard } from "@ui/components/Wizard";
import type { KeyboardEvent, ReactNode } from "react";
import { TaglineField, taglineFieldCopy } from "@/components/ListingFields/TaglineField";

const COLOR = "green" as const;
const LISTING_TYPE = "service" as const;

export interface ServiceTaglineStepViewProps {
  /** Current stage, 1-indexed, for the progress bar. */
  currentStage: number;
  /** Total stages driving the progress bar. */
  totalStages: number;
  /** Back handler; omit on the first stage to hide the back affordance. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Questions?", "Finish later"). */
  topBarActions: ReactNode;
  /** Primary "Continue" handler. */
  onPrimary: () => void;
  /** Whether the primary button shows its pending state. */
  primaryPending: boolean;
  /** Current tagline value for the textarea. */
  tagline: string;
  /** Field error message; when present the textarea renders invalid. */
  error?: string;
  /** Fires on textarea change with the new value. */
  onTaglineChange: (value: string) => void;
  /** Fires when the textarea loses focus (drives blur validation). */
  onTaglineBlur: () => void;
  /** Keydown handler that advances the wizard on Enter for this single-line-intent field. */
  onEnterSubmit: (event: KeyboardEvent) => void;
}

export const ServiceTaglineStepView = ({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  tagline,
  error,
  onTaglineChange,
  onTaglineBlur,
  onEnterSubmit,
}: ServiceTaglineStepViewProps) => {
  const { heading, helper } = taglineFieldCopy(LISTING_TYPE);

  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage heading={heading} helper={helper}>
        <div className="w-full max-w-[600px]">
          <TaglineField
            error={error}
            listingType={LISTING_TYPE}
            onBlur={onTaglineBlur}
            onChange={onTaglineChange}
            onKeyDown={onEnterSubmit}
            value={tagline}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
};
